import { useState, useEffect } from 'react'
import { PracticeVaccine, PracticeVaccineFormData } from '../types/practiceVaccine'
import { Modal } from './Modal'
import './PracticeVaccineInventory.css'

interface PracticeVaccineInventoryProps {
  vaccines: PracticeVaccine[]
  onAdd: () => void
  onEdit: (vaccine: PracticeVaccine) => void
  onDelete: (vaccine: PracticeVaccine) => void
  onSubmit: (formData: PracticeVaccineFormData) => Promise<void>
  onCancel: () => void
  showForm: boolean
  editingVaccine: PracticeVaccine | null
  isSubmitting: boolean
}

const COMMON_VACCINE_TYPES = [
  '6 in 1',
  'MenB',
  'PCV',
  'Rotavirus',
  'MMR',
  'Chickenpox',
  'MenC',
  '4 in 1',
  'HPV9',
  'MenACWY',
  'Tdap',
  'Flu',
  'Nasal Flu Vaccine',
  'PPV',
  'COVID-19',
  'Hepatitis B',
  'Hepatitis A',
  'Tetanus',
  'Diphtheria',
  'Polio',
]

const COMMON_MANUFACTURERS = [
  'GSK',
  'Pfizer',
  'MSD',
  'Sanofi',
  'AstraZeneca',
  'Moderna',
  'Novavax',
]

export function PracticeVaccineInventory({
  vaccines,
  onAdd,
  onEdit,
  onDelete,
  onSubmit,
  onCancel,
  showForm,
  editingVaccine,
  isSubmitting,
}: PracticeVaccineInventoryProps) {
  const [formData, setFormData] = useState<PracticeVaccineFormData>({
    vaccineType: '',
    batchNumber: '',
    manufacturer: '',
    expiryDate: '',
    quantity: 0,
    location: '',
    notes: '',
  })

  useEffect(() => {
    if (editingVaccine) {
      setFormData({
        vaccineType: editingVaccine.vaccineType,
        batchNumber: editingVaccine.batchNumber,
        manufacturer: editingVaccine.manufacturer,
        expiryDate: editingVaccine.expiryDate,
        quantity: editingVaccine.quantity,
        location: editingVaccine.location || '',
        notes: editingVaccine.notes || '',
      })
    } else {
      setFormData({
        vaccineType: '',
        batchNumber: '',
        manufacturer: '',
        expiryDate: '',
        quantity: 0,
        location: '',
        notes: '',
      })
    }
  }, [editingVaccine, showForm])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onSubmit(formData)
  }

  const today = new Date().toISOString().split('T')[0]
  const expiredVaccines = vaccines.filter((v) => v.expiryDate < today && v.isActive)
  const activeVaccines = vaccines.filter((v) => v.isActive && v.expiryDate >= today)
  const inactiveVaccines = vaccines.filter((v) => !v.isActive)

  return (
    <div className="practice-vaccine-inventory">
      <div className="inventory-header">
        <button className="btn btn-primary" onClick={onAdd}>
          + Add Vaccine Stock
        </button>
      </div>

      {activeVaccines.length > 0 && (
        <div className="vaccine-section">
          <h2>Active Vaccines ({activeVaccines.length})</h2>
          <table className="vaccine-table">
            <thead>
              <tr>
                <th>Vaccine Type</th>
                <th>Batch Number</th>
                <th>Manufacturer</th>
                <th>Expiry Date</th>
                <th>Quantity</th>
                <th>Location</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {activeVaccines.map((vaccine) => {
                const daysUntilExpiry = Math.floor(
                  (new Date(vaccine.expiryDate).getTime() - new Date(today).getTime()) / (1000 * 60 * 60 * 24)
                )
                const isExpiringSoon = daysUntilExpiry <= 30 && daysUntilExpiry > 0
                const isExpired = daysUntilExpiry < 0

                return (
                  <tr
                    key={vaccine.id}
                    className={isExpired ? 'expired' : isExpiringSoon ? 'expiring-soon' : ''}
                  >
                    <td>{vaccine.vaccineType}</td>
                    <td>{vaccine.batchNumber}</td>
                    <td>{vaccine.manufacturer}</td>
                    <td>
                      {vaccine.expiryDate}
                      {isExpiringSoon && <span className="warning-badge">Expiring Soon</span>}
                      {isExpired && <span className="error-badge">Expired</span>}
                    </td>
                    <td>{vaccine.quantity}</td>
                    <td>{vaccine.location || '-'}</td>
                    <td>
                      <button className="btn btn-sm btn-secondary" onClick={() => onEdit(vaccine)}>
                        Edit
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => onDelete(vaccine)}>
                        Inactivate
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {expiredVaccines.length > 0 && (
        <div className="vaccine-section">
          <h2>Expired Vaccines ({expiredVaccines.length})</h2>
          <table className="vaccine-table">
            <thead>
              <tr>
                <th>Vaccine Type</th>
                <th>Batch Number</th>
                <th>Manufacturer</th>
                <th>Expiry Date</th>
                <th>Quantity</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {expiredVaccines.map((vaccine) => (
                <tr key={vaccine.id} className="expired">
                  <td>{vaccine.vaccineType}</td>
                  <td>{vaccine.batchNumber}</td>
                  <td>{vaccine.manufacturer}</td>
                  <td>{vaccine.expiryDate}</td>
                  <td>{vaccine.quantity}</td>
                  <td>
                    <button className="btn btn-sm btn-danger" onClick={() => onDelete(vaccine)}>
                      Inactivate
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {inactiveVaccines.length > 0 && (
        <div className="vaccine-section">
          <h2>Inactive Vaccines ({inactiveVaccines.length})</h2>
          <table className="vaccine-table">
            <thead>
              <tr>
                <th>Vaccine Type</th>
                <th>Batch Number</th>
                <th>Manufacturer</th>
                <th>Expiry Date</th>
                <th>Quantity</th>
              </tr>
            </thead>
            <tbody>
              {inactiveVaccines.map((vaccine) => (
                <tr key={vaccine.id} className="inactive">
                  <td>{vaccine.vaccineType}</td>
                  <td>{vaccine.batchNumber}</td>
                  <td>{vaccine.manufacturer}</td>
                  <td>{vaccine.expiryDate}</td>
                  <td>{vaccine.quantity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {vaccines.length === 0 && (
        <div className="empty-state">
          <p>No vaccines in inventory. Click "Add Vaccine Stock" to get started.</p>
        </div>
      )}

      {showForm && (
        <Modal
          isOpen={showForm}
          title={editingVaccine ? 'Edit Vaccine Stock' : 'Add Vaccine Stock'}
          onClose={onCancel}
          size="medium"
        >
          <form onSubmit={handleSubmit} className="vaccine-form">
            <div className="form-group">
              <label htmlFor="vaccineType">
                Vaccine Type <span className="required">*</span>
              </label>
              <input
                id="vaccineType"
                type="text"
                list="vaccineTypes"
                value={formData.vaccineType}
                onChange={(e) => setFormData({ ...formData, vaccineType: e.target.value })}
                required
              />
              <datalist id="vaccineTypes">
                {COMMON_VACCINE_TYPES.map((type) => (
                  <option key={type} value={type} />
                ))}
              </datalist>
            </div>

            <div className="form-group">
              <label htmlFor="batchNumber">
                Batch Number <span className="required">*</span>
              </label>
              <input
                id="batchNumber"
                type="text"
                value={formData.batchNumber}
                onChange={(e) => setFormData({ ...formData, batchNumber: e.target.value })}
                required
                pattern="[A-Za-z0-9\-_]+"
                minLength={3}
              />
            </div>

            <div className="form-group">
              <label htmlFor="manufacturer">
                Manufacturer <span className="required">*</span>
              </label>
              <input
                id="manufacturer"
                type="text"
                list="manufacturers"
                value={formData.manufacturer}
                onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                required
              />
              <datalist id="manufacturers">
                {COMMON_MANUFACTURERS.map((mfr) => (
                  <option key={mfr} value={mfr} />
                ))}
              </datalist>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="expiryDate">
                  Expiry Date <span className="required">*</span>
                </label>
                <input
                  id="expiryDate"
                  type="date"
                  value={formData.expiryDate}
                  onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="quantity">
                  Quantity <span className="required">*</span>
                </label>
                <input
                  id="quantity"
                  type="number"
                  min="0"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 0 })}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="location">Storage Location</label>
              <input
                id="location"
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="e.g., Fridge A, Freezer B"
              />
            </div>

            <div className="form-group">
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
              />
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={isSubmitting}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : editingVaccine ? 'Update' : 'Add'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  )
}
